/**
 * Observability Profile API Client
 *
 * Lists the observability profiles (built-in and tenant-defined) that can be picked
 * as a per-execution override from Flow Studio's "Advanced Execute" modal.
 * Server-owned module: BizFirst.Ai.ProcessEngine Observability domain.
 *
 * Built-in profile IDs: Production=1, Development=2, QA=3, Eval=4.
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import type { StandardListRequest } from '../types';

/**
 * Observability Profile
 * Maps to C# ObservabilityProfile.
 */
export interface ObservabilityProfile {
  observabilityProfileID: number;
  profileName: string;
  description: string;
  isBuiltIn: boolean;
}

/**
 * Defensively read a field that may come back in either camelCase or PascalCase
 * (BaseApiClient's envelope handling round-trips response keys through a
 * PascalCase conversion before extraction — see baseApiClient.ts handleResponse
 * — so downstream consumers throughout this codebase check both casings).
 */
const readField = (source: any, camelKey: string, pascalKey: string): any =>
  source?.[camelKey] ?? source?.[pascalKey];

const normalizeProfile = (raw: any): ObservabilityProfile => ({
  observabilityProfileID: readField(raw, 'observabilityProfileID', 'ObservabilityProfileID'),
  profileName: readField(raw, 'profileName', 'ProfileName') ?? '',
  description: readField(raw, 'description', 'Description') ?? '',
  isBuiltIn: !!readField(raw, 'isBuiltIn', 'IsBuiltIn'),
});

export class ObservabilityProfileApiClient extends BaseApiClient {
  /**
   * List all observability profiles available to the current tenant.
   * POST /api/v1/obs/observability-profiles/list — a standard "list everything"
   * call (empty StandardListRequest), mirroring processApiClient.getAll's idiom.
   */
  async list(request: StandardListRequest = {}): Promise<ApiResponse<ObservabilityProfile[]>> {
    const response = await this.postQuery<any[]>(API_ENDPOINTS.OBSERVABILITY_PROFILES.LIST, request);
    if (!response.success) {
      return {
        success: false,
        data: [],
        error: response.error || {
          code: 'LIST_FAILED',
          message: 'Failed to fetch observability profiles',
        },
      };
    }
    // Handle both array and null/empty data (null is valid for empty tenant)
    const data = Array.isArray(response.data) ? response.data : [];
    return {
      ...response,
      data: data.map(normalizeProfile),
    };
  }
}

export const observabilityProfileApiClient = new ObservabilityProfileApiClient();
